import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import AdminLogoHeader from "../../components/admin/AdminLogoHeader";
import LoadingSpinner from "../../components/LoadingSpinner";
import api from "../../services/api";
import { downloadBlob } from "../../utils/download";

const initialCounts = { draft: 0, pending: 0, approved: 0, rejected: 0 };
const todayIso = () => new Date().toISOString().slice(0, 10);

const createDraftForm = () => ({
  category: "wallet_deposit",
  user_id: "",
  direction: "inflow",
  client_account_type: "wallet",
  amount: "",
  reference: "",
  transaction_date: todayIso(),
  note: "",
  bank_reference: "",
  reconciliation_note: "",
  attachment: null,
});

const BackofficePostings = () => {
  const [templates, setTemplates] = useState([]);
  const [postings, setPostings] = useState([]);
  const [counts, setCounts] = useState(initialCounts);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(createDraftForm);
  const [userOptions, setUserOptions] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [userLoading, setUserLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [exportStatus, setExportStatus] = useState("pending");
  const [exporting, setExporting] = useState(false);
  const [walletSummary, setWalletSummary] = useState({
    glBalance: 0,
    postedNet: 0,
    postedInflow: 0,
    postedOutflow: 0,
  });

  const refreshPostings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.adminListBackofficePostings();
      setPostings(response.items || []);
      setCounts({ ...initialCounts, ...(response.counts || {}) });
      setTemplates(response.templates || []);
      // compute wallet posted totals (approved only)
      const walletApproved = (response.items || []).filter(
        (item) => item.status === "approved" && item.client_account_type === "wallet"
      );
      const totals = walletApproved.reduce(
        (acc, item) => {
          const amt = Number(item.amount || 0);
          if ((item.direction || "").toLowerCase() === "outflow") {
            acc.outflow += amt;
          } else {
            acc.inflow += amt;
          }
          return acc;
        },
        { inflow: 0, outflow: 0 }
      );
      setWalletSummary((prev) => ({
        ...prev,
        postedInflow: totals.inflow,
        postedOutflow: totals.outflow,
        postedNet: totals.inflow - totals.outflow,
      }));
    } catch (err) {
      setError(err.message || "Unable to load postings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshPostings();
  }, [refreshPostings]);

  useEffect(() => {
    // Fetch GL balance (wallet liability) from finance report
    (async () => {
      try {
        const report = await api.adminFetchFinanceReport();
        const trial = report?.trial_balance || [];
        const walletRow =
          trial.find((row) => row?.account?.code === "2000") ||
          trial.find((row) => (row?.account?.code || "").startsWith("2000"));
        const debit = Number(walletRow?.debit || 0);
        const credit = Number(walletRow?.credit || 0);
        const glBalance = credit - debit;
        setWalletSummary((prev) => ({ ...prev, glBalance }));
      } catch (err) {
        console.error("finance report", err);
      }
    })();
  }, []);

  const fetchUsers = useCallback(async (query = "") => {
    setUserLoading(true);
    try {
      const list = await api.adminListBackofficeClients({ q: query });
      setUserOptions(Array.isArray(list) ? list : []);
    } catch (err) {
      setUserOptions([]);
      setError(err.message || "Unable to load clients");
    } finally {
      setUserLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const timeout = setTimeout(() => fetchUsers(userSearch.trim()), 300);
    return () => clearTimeout(timeout);
  }, [userSearch, fetchUsers]);

  useEffect(() => {
    const userId = form.user_id;
    if (!userId) {
      setSelectedUser(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const detail = await api.adminGetBackofficeClient(userId);
        if (!cancelled) setSelectedUser(detail);
      } catch (err) {
        if (!cancelled) {
          setSelectedUser(null);
          setError(err.message || "Unable to load client details");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.user_id]);

  const selectedTemplate = useMemo(
    () => templates.find((tpl) => tpl.value === form.category),
    [templates, form.category]
  );

  useEffect(() => {
    if (!selectedTemplate) return;
    setForm((prev) => {
      const nextDirection = selectedTemplate.default_direction || "inflow";
      const nextAccountType =
        selectedTemplate.client_account_types?.[0] || prev.client_account_type || "wallet";
      if (prev.direction === nextDirection && prev.client_account_type === nextAccountType) {
        return prev;
      }
      return { ...prev, direction: nextDirection, client_account_type: nextAccountType };
    });
  }, [selectedTemplate]);

  const accountTypeOptions = selectedTemplate?.client_account_types || ["wallet"];

  const recentApproved = useMemo(
    () => postings.filter((item) => item.status === "approved").slice(0, 6),
    [postings]
  );

  const handleFormChange = (event) => {
    const { name, value, type, files } = event.target;
    if (type === "file") {
      setForm((prev) => ({ ...prev, attachment: files?.[0] || null }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      if (!form.user_id) {
        setError("Please select a client to continue.");
        setSubmitting(false);
        return;
      }
      const payload = new FormData();
      payload.append("category", form.category);
      payload.append("user_id", form.user_id);
      payload.append("direction", form.direction);
      payload.append("client_account_type", form.client_account_type);
      payload.append("amount", form.amount || "0");
      if (form.reference) payload.append("reference", form.reference);
      payload.append("transaction_date", form.transaction_date || todayIso());
      if (form.note) payload.append("note", form.note);
      if (form.bank_reference) payload.append("bank_reference", form.bank_reference);
      if (form.reconciliation_note)
        payload.append("reconciliation_note", form.reconciliation_note);
      if (form.attachment) payload.append("attachment", form.attachment);

      await api.adminCreateBackofficePosting(payload);
      setSuccess("Posting captured. Review/approve it from Pending postings.");
      setForm(createDraftForm());
      refreshPostings();
    } catch (err) {
      setError(err.message || "Unable to create posting");
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = {};
      if (exportStatus) params.status = exportStatus;
      const blob = await api.adminExportBackofficePostings(params);
      const stamp = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 13);
      const suffix = exportStatus || "all";
      downloadBlob(blob, `backoffice-postings-${suffix}-${stamp}.xlsx`);
    } catch (err) {
      alert(err.message || "Unable to export postings.");
    } finally {
      setExporting(false);
    }
  };

  const formatAmount = (value, currency = "NGN") =>
    new Intl.NumberFormat("en-NG", { style: "currency", currency }).format(value || 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminLogoHeader />
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Back-office postings</h1>
              <p className="text-sm text-slate-500">
                Capture manual client movements so accounts can approve them from the unified Pending
                postings queue. Every submission hits the ledger only after approval.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-center text-sm text-slate-600 md:grid-cols-4">
              {["pending", "approved", "rejected", "draft"].map((key) => (
                <div key={key} className="rounded-xl border border-slate-100 px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-slate-400">{key}</p>
                  <p className="text-lg font-semibold text-slate-900">{counts[key] || 0}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-600 md:justify-end">
            <label htmlFor="export-status" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Export status
            </label>
            <select
              id="export-status"
              value={exportStatus}
              onChange={(event) => setExportStatus(event.target.value)}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="draft">Draft</option>
            </select>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="rounded-lg border border-emerald-200 px-3 py-1.5 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
            >
              {exporting ? "Preparing..." : "Export Excel"}
            </button>
          </div>

          <form onSubmit={handleCreate} className="mt-6 space-y-4">
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            {success ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                {success}{" "}
                <Link to="/admin/general-ledger/pending" className="underline">
                  Go to pending postings
                </Link>
              </div>
            ) : null}

            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Client</label>
                <div className="mt-1 flex gap-2">
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(event) => setUserSearch(event.target.value)}
                    placeholder="Search name or email"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                  <select
                    name="user_id"
                    value={form.user_id}
                    onChange={handleFormChange}
                    className="min-w-[150px] rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    required
                  >
                    <option value="">
                      {userLoading ? "Loading clients..." : "Select client"}
                    </option>
                    {userOptions.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name || user.username}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedUser ? (
                  <div className="mt-2 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                    <div className="font-semibold">
                      {selectedUser.full_name || selectedUser.username} · {selectedUser.username}
                    </div>
                    <div>Wallet balance: {new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(selectedUser.wallet_balance || 0)}</div>
                    <div className="text-emerald-700/80">Role: {selectedUser.role || "user"}</div>
                  </div>
                ) : null}
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Category</label>
                <select
                  name="category"
                  value={form.category}
                  onChange={handleFormChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                >
                  {templates.map((tpl) => (
                    <option key={tpl.value} value={tpl.value}>
                      {tpl.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Direction</label>
                <select
                  name="direction"
                  value={form.direction}
                  onChange={handleFormChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                >
                  <option value="inflow">Inflow (credit client)</option>
                  <option value="outflow">Outflow (debit client)</option>
                </select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Client account</label>
                <select
                  name="client_account_type"
                  value={form.client_account_type}
                  onChange={handleFormChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                >
                  {accountTypeOptions.includes("wallet") && <option value="wallet">Wallet</option>}
                  {accountTypeOptions.includes("investment") && (
                    <option value="investment">Investment</option>
                  )}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Amount (NGN)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  name="amount"
                  value={form.amount}
                  onChange={handleFormChange}
                  required
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Reference</label>
                <input
                  type="text"
                  name="reference"
                  value={form.reference}
                  onChange={handleFormChange}
                  placeholder="Optional"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Transaction date</label>
                <input
                  type="date"
                  name="transaction_date"
                  value={form.transaction_date}
                  onChange={handleFormChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-slate-700">Narration / Notes</label>
                <textarea
                  name="note"
                  rows={3}
                  value={form.note}
                  onChange={handleFormChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Visible to the accounts team"
                />
              </div>
              <div className="grid gap-3 rounded-2xl border border-slate-200 p-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">Bank reference</label>
                  <input
                    type="text"
                    name="bank_reference"
                    value={form.bank_reference}
                    onChange={handleFormChange}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    placeholder="Receipt / teller number"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">
                    Reconciliation note (internal)
                  </label>
                  <input
                    type="text"
                    name="reconciliation_note"
                    value={form.reconciliation_note}
                    onChange={handleFormChange}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    placeholder="Matched to bank statement..."
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Attach evidence</label>
              <input
                type="file"
                name="attachment"
                onChange={handleFormChange}
                className="mt-1 block w-full text-sm text-slate-500 file:mr-4 file:rounded-lg file:border-0 file:bg-emerald-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-emerald-700 hover:file:bg-emerald-100"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {submitting ? "Saving..." : "Submit for approval"}
            </button>
          </form>
        </section>

        <section className="rounded-2xl border border-emerald-100 bg-emerald-50 px-6 py-4 text-sm text-emerald-900">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              Once submitted, accounts can approve or reject the entry from{" "}
              <Link to="/admin/general-ledger/pending" className="font-semibold underline">
                Finance > Pending postings
              </Link>
              . Approved entries automatically post to the ledger (and wallet, when applicable).
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-emerald-800">
              <div className="rounded-lg bg-white/60 px-3 py-2 shadow-sm">
                <div className="font-semibold">Wallet liability (GL)</div>
                <div className="tabular-nums">
                  {new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(
                    walletSummary.glBalance || 0
                  )}
                </div>
              </div>
              <div className="rounded-lg bg-white/60 px-3 py-2 shadow-sm">
                <div className="font-semibold">Approved wallet postings (net)</div>
                <div className="tabular-nums">
                  {new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(
                    walletSummary.postedNet || 0
                  )}
                </div>
                <div className="text-[11px] text-emerald-700/80">
                  In: {new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(walletSummary.postedInflow || 0)} ·
                  Out: {new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(walletSummary.postedOutflow || 0)}
                </div>
              </div>
              <div className="rounded-lg bg-white/60 px-3 py-2 shadow-sm">
                <div className="font-semibold">Difference (GL - posted)</div>
                <div
                  className={`tabular-nums ${
                    Math.abs((walletSummary.glBalance || 0) - (walletSummary.postedNet || 0)) < 0.01
                      ? "text-emerald-700"
                      : "text-amber-700"
                  }`}
                >
                  {new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(
                    (walletSummary.glBalance || 0) - (walletSummary.postedNet || 0)
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-6 py-4">
            <h2 className="text-lg font-semibold text-slate-900">Recent approved postings</h2>
            <p className="text-sm text-slate-500">
              Quick glance at the last few approvals for reconciliation audit.
            </p>
          </div>
          {loading ? (
            <div className="px-6 py-6">
              <LoadingSpinner />
            </div>
          ) : recentApproved.length === 0 ? (
            <p className="px-6 py-4 text-sm text-slate-500">No approved postings yet.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentApproved.map((item) => (
                <div key={item.id} className="flex flex-col gap-1 px-6 py-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {item.reference} - {item.category_label}
                    </p>
                    <p className="text-sm text-slate-500">
                      {item.user?.full_name || item.user?.username || "N/A"} -{" "}
                      {formatAmount(item.amount, item.currency)} - {item.client_account_type}
                    </p>
                  </div>
                  <div className="text-xs text-slate-400">
                    Batch #{item.ledger_batch_id} -{" "}
                    {item.approved_at ? new Date(item.approved_at).toLocaleString() : "-"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default BackofficePostings;
